/**
 * TypeScript types for Ambulance Orders feature.
 */

// =============================================================================
// Enums
// =============================================================================

export type OrderType = 'non_urgent' | 'emergency'
export type OrderStatus = 'pending' | 'reviewed'
export type ChangeType = 'create' | 'update' | 'review'

// =============================================================================
// API Request Types
// =============================================================================

export interface AmbulanceOrderCreate {
  order_type: OrderType
  orderer_name: string
  orderer_phone: string
  cost_center_id?: string
  pickup_department: string
  patient_name: string
  // order_id_number is auto-generated by backend (starting from 1000)
  patient_ssn: string
  pickup_location: string
  destination_location: string
  pickup_date: string // ISO date string YYYY-MM-DD
  pickup_time: string // HH:MM format
}

export interface AmbulanceOrderUpdate {
  order_type?: OrderType
  orderer_name?: string
  orderer_phone?: string
  cost_center_id?: string | null
  pickup_department?: string
  patient_name?: string
  order_id_number?: string | null
  patient_ssn?: string
  pickup_location?: string
  destination_location?: string
  pickup_date?: string
  pickup_time?: string
  comment?: string // Optional comment explaining the change
}

export interface AmbulanceOrderReview {
  comment?: string // Optional review comment
}

// =============================================================================
// API Response Types
// =============================================================================

export interface AmbulanceOrder {
  id: string
  organization_id: string
  order_type: OrderType
  orderer_name: string
  orderer_phone: string
  cost_center_id: string | null
  cost_center_name: string | null
  pickup_department: string
  patient_name: string
  order_id_number: string | null
  patient_ssn: string // Decrypted SSN
  pickup_location: string
  destination_location: string
  pickup_date: string
  pickup_time: string
  status: OrderStatus
  reviewed_by: string | null
  reviewed_by_name: string | null
  reviewed_at: string | null
  review_comment: string | null
  created_by: string
  created_by_name: string | null
  created_at: string
  updated_at: string | null
  has_comments: boolean
}

export interface AmbulanceOrderListItem {
  id: string
  order_type: OrderType
  orderer_name: string
  patient_name: string
  pickup_date: string
  pickup_time: string
  status: OrderStatus
  cost_center_name: string | null
  created_at: string
  has_comments: boolean
}

export interface AmbulanceOrderChange {
  id: string
  order_id: string
  change_type: ChangeType
  changed_by: string
  changed_by_name: string | null
  changed_at: string
  comment: string | null
  changes: Record<string, { old: string | null; new: string | null }>
}

export interface PaginationInfo {
  page: number
  page_size: number
  total_items: number
  total_pages: number
}

export interface AmbulanceOrderListResponse {
  data: AmbulanceOrderListItem[]
  pagination: PaginationInfo
}

export interface AmbulanceOrderDetailResponse {
  order: AmbulanceOrder
  changes: AmbulanceOrderChange[]
}

// =============================================================================
// Filter Types
// =============================================================================

export interface AmbulanceOrderFilters {
  status?: OrderStatus
  cost_center_id?: string
  date_from?: string
  date_to?: string
  patient_name?: string
  patient_ssn?: string
}

// =============================================================================
// Form State Types
// =============================================================================

export interface AmbulanceOrderFormData {
  order_type: OrderType
  orderer_name: string
  orderer_phone: string
  cost_center_id: string
  pickup_department: string
  patient_name: string
  // order_id_number is auto-generated by backend (not user input)
  patient_ssn: string
  pickup_location: string
  destination_location: string
  pickup_date: string
  pickup_time: string
}

export const EMPTY_ORDER_FORM: AmbulanceOrderFormData = {
  order_type: 'non_urgent',
  orderer_name: '',
  orderer_phone: '',
  cost_center_id: '',
  pickup_department: '',
  patient_name: '',
  patient_ssn: '',
  pickup_location: '',
  destination_location: '',
  pickup_date: '',
  pickup_time: '',
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Finnish SSN pattern:
 * - 6 digits for birthdate (DDMMYY)
 * - Separator: - (1900s), + (1800s), A (2000s), or other letters
 * - 3 digits + check character
 */
export const FINNISH_SSN_PATTERN = /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(\d{2})[-+A-FU-Y](\d{3}[\dA-Z])$/i

export function validateFinnishSSN(ssn: string): boolean {
  return FINNISH_SSN_PATTERN.test(ssn)
}

export function formatSSNForDisplay(ssn: string): string {
  // Mask middle characters for display in lists
  if (ssn.length >= 11) {
    return `${ssn.substring(0, 6)}****${ssn.substring(10)}`
  }
  return ssn
}
